import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, mapTo, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { padNum } from '../common/common.util';
import { mapList, mapRetrieve, mapUpdate } from '../common/rest.operators';
import { PeriodType } from '../widget/date-range.widget.model';

import {
    Category,
    Config,
    ConfigTransformer,
    DataAuthorizationModel,
    DefinitionSummaryTransformer,
    HierarchyUserInfoItem,
    Override,
    RoleHierarchyItem,
} from './config.insight.model';
import { MetricThreshold, PerformanceTrend } from './legend.insight.model';
import { SuperGroupQueue } from './models/super-group-queue.model';

@Injectable()
export class InsightService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listDefinitionSummary(components?: string[]): Observable<Category[]> {
        let params = new HttpParams();
        if (components != null) {
            // eslint-disable-next-line no-return-assign
            components.forEach(
                (e) => (params = params.append('components', e)),
            );
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/definition/summary`,
                { params },
            )
            .pipe(
                mapList(new DefinitionSummaryTransformer()),
                catchError(() => of([])),
            );
    }

    retrieveOverride<T>(): Observable<Override<T>> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.INSIGHT}/config/override`)
            .pipe(
                mapRetrieve(),
                catchError(() => of(null)),
            );
    }

    updateOverride<T>(override: Override<T>): Observable<boolean> {
        return this._http
            .put(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/override`,
                override,
            )
            .pipe(
                mapUpdate(),
                mapTo(true),
                catchError(() => of(false)),
            );
    }

    listPermissions(): Observable<DataAuthorizationModel[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.INSIGHT}/config/permissions`)
            .pipe(
                mapList(),
                catchError(() => of(null)),
            );
    }

    retrieveConfigShallow(
        identifier: string,
        component?: string,
    ): Observable<Config> {
        let params = new HttpParams();
        if (component != null) {
            params = params.set('component', component);
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/${identifier}/shallow`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError(() => of({})),
            );
    }

    retrieveConfig(
        identifier: string,
        component?: string,
        criteriaId?: number,
        userId?: number,
        periodType?: PeriodType,
        eventDate?: Date,
    ): Observable<Config> {
        let params = new HttpParams();
        if (component != null) {
            params = params.set('component', component);
        }
        if (criteriaId != null) {
            params = params.set('criteriaId', criteriaId.toString());
        }
        if (userId != null) {
            params = params.set('userId', userId.toString());
        }
        if (periodType != null) {
            params = params.set('periodType', periodType.toString());
        }
        if (eventDate && !isNaN(eventDate.getTime())) {
            params = params.set(
                'eventDate',
                `${eventDate.getFullYear()}-${padNum(
                    eventDate.getMonth() + 1,
                    2,
                )}-${padNum(eventDate.getDate(), 2)}`,
            );
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/${identifier}`,
                { params },
            )
            .pipe(
                mapRetrieve(new ConfigTransformer()),
                catchError(() => of({} as any as Config)),
            );
    }

    listRoleHierarchy(): Observable<RoleHierarchyItem[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/roleHierarchy`,
            )
            .pipe(
                mapList(),
                tap((result: RoleHierarchyItem[]) => {
                    result.forEach((e) => {
                        e.startDate = new Date(e.startDate);
                        e.endDate = new Date(e.endDate);
                    });
                }),
                catchError(() => of([])),
            );
    }

    listHierarchyUserInfo(
        userIds: number[],
        eventDate?: Date,
    ): Observable<HierarchyUserInfoItem[]> {
        const eventDateStr =
            eventDate &&
            !isNaN(eventDate.getTime()) &&
            `${eventDate.getFullYear()}-${padNum(
                eventDate.getMonth() + 1,
                2,
            )}-${padNum(eventDate.getDate(), 2)}`;
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/hierarchyUserInfo`,
                {
                    userIds,
                    eventDate: eventDateStr,
                },
            )
            .pipe(
                mapList(),
                catchError(() => of([])),
            );
    }

    listSubordinatesHierarchyUserInfo(
        obj: any,
    ): Observable<HierarchyUserInfoItem[]> {
        const eventDateStr =
            obj.eventDate &&
            !isNaN(obj.eventDate.getTime()) &&
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/restrict-plus-operands
            `${obj.eventDate.getFullYear()}-${padNum(
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                obj.eventDate.getMonth() + 1,
                2,
            )}-${padNum(obj.eventDate.getDate(), 2)}`;
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/subordinatesHierarchyUserInfo`,
                {
                    ...obj,
                    scopeId: 4,
                    eventDate: eventDateStr,
                },
            )
            .pipe(
                mapList(),
                catchError(() => of([])),
            );
    }

    listMetricThresholds(metricIds: number[]): Observable<MetricThreshold[]> {
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/metricThreshold`,
                { metricIds },
            )
            .pipe(
                mapList(),
                catchError(() => of([])),
            );
    }

    listTrendsArrows(): Observable<PerformanceTrend[]> {
        return this._http
            .get(`${this._appConfig.API_ENDPOINTS.INSIGHT}/config/trend`)
            .pipe(
                mapList(),
                catchError(() => of([])),
            );
    }

    listSuperGroupQueues(
        eventDate: Date,
        queuesIds?: number[],
        sgIds?: number[],
        userIds?: number[],
    ): Observable<SuperGroupQueue[]> {
        const payload = {
            eventDate,
            queuesIds,
            sgIds,
            userIds,
        };
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/supergroupQueues`,
                payload,
            )
            .pipe(
                mapList(),
                catchError(() => of([])),
            );
    }
}
